import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChartPie, AlertTriangle, BarChart3 } from "lucide-react";
import SectorVerdictPill from "@/components/common/SectorVerdictPill";

/**
 * SuggestionSummaryPanel — Phase 9
 *
 * "Portfolio Analysis Summary" card shown at the top of:
 * - New portfolio creation suggestion list
 * - Monthly investment suggestion dialog
 *
 * Renders sector conditions, data-confidence breakdown, and warnings.
 */
const VERDICT_COLOUR = {
  OVERWEIGHT: { dot: "bg-[hsl(var(--success))]", text: "text-[hsl(var(--success))]", label: "OVERWEIGHT" },
  UNDERWEIGHT: { dot: "bg-[hsl(var(--warning))]", text: "text-[hsl(var(--warning))]", label: "UNDERWEIGHT" },
  NEUTRAL: { dot: "bg-muted-foreground", text: "text-muted-foreground", label: "NEUTRAL" },
};

const ConfidenceRow = ({ label, count, dot }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
    <span className="font-medium capitalize">{label}:</span>
    <span className="text-muted-foreground tabular-nums">{count} stock{count === 1 ? "" : "s"}</span>
  </div>
);

const SuggestionSummaryPanel = ({
  summary,
  investmentAmount,
  title = "Portfolio Analysis Summary",
  testId = "suggestion-summary-panel",
  macroGeo = null,
  warnings = [],
}) => {
  if (!summary) return null;
  const conditions = summary.sector_conditions || {};
  const conf = summary.data_confidence_counts || {};
  const lowWarn = summary.low_confidence_warning || 0;
  const mgAvailable = macroGeo && macroGeo.available;
  const favours = (macroGeo && macroGeo.favours) || [];
  const cautions = (macroGeo && macroGeo.cautions) || [];

  return (
    <Card
      className="border-2 border-primary/30 bg-primary/5"
      data-testid={testId}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ChartPie className="h-4 w-4 text-primary" />
          <h4 className="font-semibold text-sm">{title}</h4>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
          {investmentAmount != null && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Investment
              </div>
              <div className="font-display font-semibold tabular-nums">
                Rs. {Number(investmentAmount).toLocaleString()}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Stocks selected
            </div>
            <div className="font-display font-semibold tabular-nums">
              {summary.stocks_selected ?? 0}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Sectors covered
            </div>
            <div className="font-display font-semibold tabular-nums">
              {summary.sectors_covered ?? 0}
            </div>
          </div>
          {summary.eligible_buy_pool != null && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                BUY pool
              </div>
              <div className="font-display font-semibold tabular-nums">
                {summary.eligible_buy_pool}
              </div>
            </div>
          )}
        </div>

        {Object.keys(conditions).length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Sector conditions
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(conditions).map(([sector, verdict]) => (
                /* Test 6 Fix 4 — canonical verdict pill (OVER/NEUT/UNDER)
                   so colour coding matches Sectors page, screener, and
                   stock detail. */
                <span
                  key={sector}
                  className="inline-flex items-center gap-1.5"
                  data-testid={`${testId}-sector-${sector}`}
                >
                  <span className="text-[10px] text-muted-foreground">
                    {sector}
                  </span>
                  <SectorVerdictPill verdict={verdict} size="sm" />
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Data confidence
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <ConfidenceRow label="High" count={conf.high || 0} dot="bg-[hsl(var(--success))]" />
            <ConfidenceRow label="Medium" count={conf.medium || 0} dot="bg-[hsl(var(--chart-2))]" />
            <ConfidenceRow label="Low" count={conf.low || 0} dot="bg-[hsl(var(--warning))]" />
            <ConfidenceRow label="Minimal" count={conf.minimal || 0} dot="bg-[hsl(var(--destructive))]" />
          </div>
        </div>

        {lowWarn > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5 p-2.5 text-[11px] leading-snug">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--warning))] shrink-0" />
            <span>
              <span className="font-medium">{lowWarn}</span> stock{lowWarn === 1 ? "" : "s"} have low or minimal data confidence.
              Upload annual reports for more accurate scoring before
              committing significant capital.
            </span>
          </div>
        )}

        {/* Phase 7 (Deep Analysis) — Macro & Geo Conditions */}
        {mgAvailable && (favours.length > 0 || cautions.length > 0) && (
          <div
            className="rounded-md border bg-card p-2.5 space-y-2"
            data-testid={`${testId}-macrogeo`}
          >
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <span>Macro &amp; Geo Conditions</span>
              <span className="text-muted-foreground/70">
                · refreshed weekly from CBSL &amp; news feeds
              </span>
            </div>
            {favours.length > 0 && (
              <div className="text-xs">
                <div className="text-[hsl(var(--success))] font-medium mb-0.5">
                  Current conditions favour:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {favours.map((f) => (
                    <span
                      key={f.sector}
                      className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-2 py-0.5 text-[hsl(var(--success))]"
                      title={`Macro: ${f.macro_verdict} · Geo: ${f.geo_verdict}`}
                    >
                      {f.macro_emoji}
                      {f.geo_emoji}
                      <span className="font-medium">{f.sector}</span>
                      {f.in_portfolio && (
                        <span className="text-[10px] uppercase tracking-wide opacity-80">
                          · held
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {cautions.length > 0 && (
              <div className="text-xs">
                <div className="text-amber-600 dark:text-amber-400 font-medium mb-0.5">
                  Approach with caution:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cautions.map((c) => (
                    <span
                      key={c.sector}
                      className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-amber-700 dark:text-amber-400"
                      title={`Macro: ${c.macro_verdict} · Geo: ${c.geo_verdict}`}
                    >
                      {c.macro_emoji}
                      {c.geo_emoji}
                      <span className="font-medium">{c.sector}</span>
                      {c.in_portfolio && (
                        <span className="text-[10px] uppercase tracking-wide opacity-80">
                          · held
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {warnings && warnings.length > 0 && (
          <div
            className="space-y-1"
            data-testid={`${testId}-macrogeo-warnings`}
          >
            {warnings.slice(0, 4).map((w, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] leading-snug text-amber-700 dark:text-amber-300"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SuggestionSummaryPanel;
